import { Component, OnInit, ViewChild } from '@angular/core';
import { MatDialogRef } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { User } from 'app/classes/user';
import { FileService } from 'app/core/services/file.service';
import { DocumentService } from 'app/document/services/document.service';
import { AuthService } from 'app/services/auth.service';
import { UserDatabaseService } from 'app/services/user-database.service';
import { DocumentDatabaseService } from 'fitforce-document-sync';
import { cloneDeep, filter, find, forEach, map, some } from 'lodash';
import * as moment from 'moment';
import { empty, from } from 'rxjs';
import { catchError, concatMap } from 'rxjs/operators';
import { v4 as uuid } from 'uuid';

@Component({
  selector: 'import-database-dialog',
  templateUrl: './import-database-dialog.component.html',
  styleUrls: ['./import-database-dialog.component.scss'],
})
export class ImportDatabaseDialogComponent implements OnInit {
  @ViewChild('file', { static: true }) fileElement;
  fileObj: File;
  fileContents: any;
  inProgress = false;
  complete = false;
  plans: any[] = [];
  isDocument = false;
  currentUser: User;

  constructor(
    public dialog: MatDialogRef<ImportDatabaseDialogComponent>,
    private databaseService: UserDatabaseService,
    private documentDatabaseService: DocumentDatabaseService,
    private fileService: FileService,
    private documentService: DocumentService,
    private snackbar: MatSnackBar,
    private authService: AuthService,
  ) {}

  ngOnInit() {
    this.authService.currentUserObs.subscribe((currentUser: User) => {
      this.currentUser = currentUser;
    });
  }

  /**
   * triggers a click event on the hidden input file type element
   */
  triggerClick() {
    this.fileElement.nativeElement.click();
  }

  /**
   * sets the file object and contents on the scope and populates plans to select from
   */
  setFile() {
    this.complete = false;
    this.fileObj = this.fileElement.nativeElement.files.length > 0 ? this.fileElement.nativeElement.files[0] : null;
    this.plans = [];
    this.isDocument = false;

    // read json file
    from(this.fileService.readJsonFile(this.fileObj))
      .pipe(
        catchError(err => {
          this.fileObj = null;
          this.fileContents = null;
          this.plans = [];
          this.snackbar.open(err.message, 'Dismiss');
          return empty();
        }),
      )
      .subscribe((contents: any) => {
        // pull out plans from contents to select from
        this.fileContents = contents;
        if (
          (contents[UserDatabaseService.dbName] && contents[UserDatabaseService.dbName].hasOwnProperty('plans')) ||
          (contents[DocumentDatabaseService.dbName] && contents[DocumentDatabaseService.dbName].hasOwnProperty('planDocuments'))
        ) {
          const plans = contents[UserDatabaseService.dbName]
            ? contents[UserDatabaseService.dbName].plans
            : contents[DocumentDatabaseService.dbName]
            ? contents[DocumentDatabaseService.dbName].planDocuments
            : [];

          plans.forEach((plan: any) => {
            this.isDocument = plan.hasOwnProperty('document');
            this.plans.push({
              planUUID: plan.key,
              plan: plan,
              name: plan.name || plan.document.name,
              date: new Date(plan.startDate || plan.document.start_date),
              checked: false,
            });
          });
        }
      });
  }

  /**
   * Reads uploaded file, converts to a javascript object, imports to indexedDB, and refreshes the displayed data
   */
  uploadFile() {
    this.inProgress = true;

    // filter plan data by selected plans
    this.fileContents[this.isDocument ? DocumentDatabaseService.dbName : UserDatabaseService.dbName] = this.filterPlans();

    // make copies of plans not owned by importer
    this.fileContents[this.isDocument ? DocumentDatabaseService.dbName : UserDatabaseService.dbName] = this.copySharedPlans();

    from((this.isDocument ? this.documentDatabaseService : this.databaseService).importFromJSON(this.fileContents))
      .pipe(
        concatMap(() => {
          if (this.isDocument) {
            return this.documentService.restoreData();
          } else {
            return;
          }
        }),
        catchError(err => {
          this.inProgress = false;
          this.complete = true;
          this.snackbar.open('Error: ' + err.message, 'Dismiss');
          return empty();
        }),
      )
      .subscribe(() => {
        this.inProgress = false;
        this.complete = true;
      });
  }

  /**
   * filters plans by selected
   */
  private filterPlans(): any[] {
    const dbImport = this.fileContents[this.isDocument ? DocumentDatabaseService.dbName : UserDatabaseService.dbName];
    if (this.isDocument) {
      dbImport.plans = this.filterObjectStore('plans', 'key');
      dbImport.planDocuments = this.filterObjectStore('planDocuments', 'key');
    } else {
      dbImport.plans = this.filterObjectStore('plans', 'key');
      dbImport.planDays = this.filterObjectStore('planDays', 'planUUID');
      dbImport.planPhases = this.filterObjectStore('planPhases', 'planUUID');
      dbImport.planDayBlocks = this.filterObjectStore(
        'planDayBlocks',
        'dayUUID',
        map(this.filterObjectStore('planDays', 'planUUID'), day => day.key),
      );
    }

    return dbImport;
  }

  /**
   * filters object stores in import file by checked plans
   * @param objectStoreName object store name
   * @param propertyName property name to filter by
   * @param filterByIds ids to filter by - defaulted to selected plan ids
   */
  private filterObjectStore(
    objectStoreName: string,
    propertyName: string,
    filterByIds: any[] = map(this.plans, plan => plan.checked && plan.planUUID),
  ): any[] {
    const dbImport = this.fileContents[this.isDocument ? DocumentDatabaseService.dbName : UserDatabaseService.dbName];
    if (dbImport.hasOwnProperty(objectStoreName)) {
      // filter object store to only include plans within filterByIds
      return propertyName
        ? filter(dbImport[objectStoreName], obj => some(filter(filterByIds, plan => plan), id => id === obj[propertyName]))
        : dbImport[objectStoreName];
    } else {
      return [];
    }
  }

  /**
   * copies any selected plans and related entities not created by importing user
   */
  private copySharedPlans(): any[] {
    const dbImport = this.fileContents[this.isDocument ? DocumentDatabaseService.dbName : UserDatabaseService.dbName];

    if (dbImport.hasOwnProperty('plans')) {
      forEach(dbImport.plans, plan => {
        if (plan.createdByUser.userUUID !== this.currentUser.uuid) {
          const newPlanUUID = uuid();

          if (dbImport.hasOwnProperty('planPhases')) {
            // update planUUID of related phases
            forEach(filter(dbImport.planPhases, phase => phase.planUUID === plan.key), phase => {
              phase.key = uuid();
              phase.planUUID = newPlanUUID;
              phase.createdAt = moment().toISOString();
            });
          }

          if (dbImport.hasOwnProperty('planDays')) {
            // update planUUID of related days
            forEach(filter(dbImport.planDays, day => day.planUUID === plan.key), day => {
              const newDayUUID = uuid();

              if (dbImport.hasOwnProperty('planDayBlocks')) {
                // update dayUUID of related day blocks
                forEach(filter(dbImport.planDayBlocks, block => block.dayUUID === day.key), block => {
                  // update key of planned exercises if any exist
                  forEach(block.rows, row => {
                    if (row.hasOwnProperty('plannedExerciseUUID')) {
                      row.plannedExerciseUUID = uuid();
                    }
                  });

                  if (block.isCustom && dbImport.hasOwnProperty('tiers')) {
                    // copy custom tier if plan day block is custom
                    const customTier = find(dbImport.tiers, tier => tier.key === block.tierUUID);
                    if (customTier) {
                      const newTier = cloneDeep(customTier);
                      newTier.key = uuid();
                      newTier.uuid = newTier.key;
                      block.tierUUID = newTier.key;

                      if (dbImport.hasOwnProperty('workoutTypes')) {
                        // copy workout type if plan day block is custom
                        const customWorkoutType = find(dbImport.workoutTypes, workoutType => workoutType.key === customTier.workoutType);
                        if (customWorkoutType) {
                          const newWorkoutType = cloneDeep(customWorkoutType);
                          newWorkoutType.key = uuid();
                          newWorkoutType.uuid = newWorkoutType.key;
                          newTier.workoutTypeUUID = newWorkoutType.key;

                          dbImport.workoutTypes.push(newWorkoutType);
                        }
                      }

                      dbImport.tiers.push(newTier);
                    }
                  }

                  block.key = uuid();
                  block.uuid = block.key;
                  block.dayUUID = newDayUUID;
                  block.createdAt = moment().toISOString();
                });
              }

              day.key = newDayUUID;
              day.uuid = newDayUUID;
              day.planUUID = newPlanUUID;
              day.createdAt = moment().toISOString();
            });
          }

          // transfer plan ownership to current user and update properties
          if (this.isDocument) {
            plan.version = 1;
            plan.sharedWithUsers = [];
            plan.userUUID = this.currentUser.uuid;
            plan.isDownloaded = true;

            if (dbImport.hasOwnProperty('planDocuments')) {
              const foundIndex = dbImport.planDocuments.findIndex(doc => doc.key === plan.key);
              if (foundIndex > -1) {
                dbImport.planDocuments[foundIndex].key = newPlanUUID;
                dbImport.planDocuments[foundIndex].document.id = newPlanUUID;
                dbImport.planDocuments[foundIndex].version = 1;
                dbImport.planDocuments[foundIndex].name = `Copy of ${plan.name}`;
                dbImport.planDocuments[foundIndex].userUUID = this.currentUser.uuid;
                dbImport.planDocuments[foundIndex].createdAt = moment().toISOString();
              }
            }
          } else {
            plan.reviewing = false;
            plan.groupUUIDs = [];
            plan.published = false;
            plan.sharedWithUUIDs = [];
          }

          plan.createdByUser = {
            userUUID: this.currentUser.uuid,
            userEmail: this.currentUser.email,
            firstName: this.currentUser.firstName,
            lastName: this.currentUser.lastName,
          };
          plan.key = newPlanUUID;
          plan.name = `Copy of ${plan.name}`;
          plan.uuid = newPlanUUID;
          plan.createdAt = moment().toISOString();
        }
      });
    }

    return dbImport;
  }
}
